import { useState, type FC } from "react";
import Title from "../../atoms/typography/Title";
import { theme } from "../../../theme";
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ListTabBar from "../../molecules/ListTabBar";
import Card from "../../atoms/Card";
import CardBody from "../../atoms/CardBody";
import { Col, Row } from "react-grid-system";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import { SortingArrowsIcon } from "../../atoms/icons/SortingArrowsIcon";
import styled from "styled-components";
import { useAllMonitorEvents } from "../../../graphql/hooks/aml/useAllMonitorEvents";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import Alert from "../../molecules/Alert";
import BodySmall from "../../atoms/typography/BodySmall";
import Small from "../../atoms/typography/Small";
import { MonitorTypes, getMonitorEventTitles } from "../../../utils/amlHelpers";
import dayjs from "dayjs";
import IconButton from "../../atoms/buttons/IconButton";
import { OpenLinkIcon } from "../../atoms/icons/OpenLinkIcon";
import PaginationComponent from "../../molecules/PaginationComponent";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { RefreshIcon } from "../../atoms/icons/RefreshIcon";
import MultiSelect from "../../atoms/inputs/MultiSelect";
import DateRangePicker from "../../molecules/filters/DateRangePicker";
import HandleMonitorEventButton from "../../molecules/aml/HandleMonitorEventButton";
import HandleMonitorEventModal from "../../molecules/aml/HandleMonitorEventModal";
import Tooltip from "../../molecules/Tooltip";
import Body from "../../atoms/typography/Body";
import CircleIcon from "../../atoms/CircleIcon";
import TableHeader from "../../atoms/TableHeader";
import TableRow from "../../atoms/TableRow";

const TABS = [
  { tabLabelKey: "aml.unhandled", id: "unhandled" },
  { tabLabelKey: "aml.handled", id: "handled" },
  { tabLabelKey: "common.all", id: "all" },
];

const FlexedDivCentered = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  &:hover {
    cursor: pointer;
    filter: opacity(0.6);
  }
`;

const ITEMS_PER_PAGE = 20;

const AmlMonitoringEvents: FC = () => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<
    "unhandled" | "handled" | "all"
  >("unhandled");
  const [currentPage, setCurrentPage] = useState(0);

  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [createdFrom, setCreatedFrom] = useState<Date | undefined>(undefined);
  const [createdTo, setCreatedTo] = useState<Date | undefined>(undefined);
  const [selectedEventTypes, setSelectedEventTypes] = useState<
    undefined | Array<string>
  >(undefined);
  const [sortBy, setSortBy] = useState<
    "CREATED_AT" | "ISSUER_CLIENT_IDENTIFIER" | "ISSUER_CLIENT_NAME"
  >("CREATED_AT");
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null);

  const { monitorEvents, error, loading, refetch } = useAllMonitorEvents(
    sortDirection,
    createdFrom,
    createdTo,
    selectedTab === "all" ? undefined : selectedTab === "handled",
    selectedEventTypes,
    currentPage * ITEMS_PER_PAGE,
    ITEMS_PER_PAGE,
    sortBy,
  );

  const onClickSortByDate = () => {
    setSortBy("CREATED_AT");
    setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
  };
  const onClickSortByClientName = () => {
    setSortBy("ISSUER_CLIENT_NAME");
    setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
  };

  const onSelectEvents = (selectedOptions: string[]) => {
    setSelectedEventTypes(selectedOptions);
  };

  const onSelectDates = ({ from, to }: { from?: Date; to?: Date }) => {
    setCreatedFrom(from);
    setCreatedTo(to);
  };

  const items = monitorEvents?.items ?? [];
  const selectedEvent = openModalIndex !== null ? items[openModalIndex] : null;

  return (
    <div>
      {selectedEvent && (
        <HandleMonitorEventModal
          key={selectedEvent.id}
          clientIdentifier={selectedEvent.clientIdentifier}
          clientName={selectedEvent.clientName}
          monitorEventId={selectedEvent.id}
          eventTitle={t(
            getMonitorEventTitles(selectedEvent.eventType) ??
              selectedEvent.eventType,
          )}
          eventDate={selectedEvent.detectedAt}
          descriptionLines={selectedEvent.descriptionLines}
          parameters={selectedEvent.parameters}
          parameterExclusionOptions={selectedEvent.parameterExclusionOptions}
          comments={selectedEvent.comments?.items || []}
          refetch={refetch}
          onClose={() => setOpenModalIndex(null)}
          onHandled={() => refetch()}
          onPrev={() => setOpenModalIndex((i) => (i ?? 0) - 1)}
          onNext={() => setOpenModalIndex((i) => (i ?? 0) + 1)}
          hasPrev={(openModalIndex ?? 0) > 0}
          hasNext={(openModalIndex ?? 0) < items.length - 1}
        />
      )}
      <Title color={theme.colors.primary}>{t("aml.monitoringEvents")}</Title>
      <HorizontalSpacer spacing={2} />
      <ListTabBar
        tabs={TABS}
        selectedId={selectedTab}
        onSelectTabId={setSelectedTab}
      />
      <HorizontalSpacer spacing={1.5} />
    
      <Row nogutter justify="between" align="end">
        <div style={{ display: "flex", gap: 16 }}>
          <MultiSelect
            options={MonitorTypes}
            selectedOptions={selectedEventTypes ?? []}
            onChange={onSelectEvents}
            onClickSelectAll={() => setSelectedEventTypes(undefined)}
            title="Händelsetyp"
          />
          <DateRangePicker
            from={createdFrom}
            to={createdTo}
            onSelect={({ from, to }) => {
              onSelectDates({ from, to });
            }}
          />
        </div>
        <OutlinedButton
          title={t("common.refresh")}
          onClick={() => refetch()}
          icon={<RefreshIcon color={theme.colors.primary} />}
          color={theme.colors.primary}
        />
      </Row>
      <HorizontalSpacer spacing={1.5} />
      <Card spacing={0} border>
        <TableHeader>
          <Col lg={3}>
            <FlexedDivCentered onClick={onClickSortByClientName}>
              <div style={{ width: 32 }} />
              <TableHeaderText>{t("common.client")}</TableHeaderText>
              <SortingArrowsIcon />
            </FlexedDivCentered>
          </Col>
          <Col lg={3}>
            <TableHeaderText>{t("aml.event")}</TableHeaderText>
          </Col>
          <Col lg={2}>
            <FlexedDivCentered onClick={onClickSortByDate}>
              <TableHeaderText>{t("dates.date")}</TableHeaderText>
              <SortingArrowsIcon />
            </FlexedDivCentered>
          </Col>
          <Col lg={3}>
            <TableHeaderText>{t("aml.comment")}</TableHeaderText>
          </Col>
        </TableHeader>
        {error && (
          <CardBody>
            <Alert
              type="error"
              title={t("errors.somethingWentWrong")}
              message={error?.toString()}
            />
          </CardBody>
        )}

        {loading ? (
          <CardBody>
            <LoadingSpinner />
          </CardBody>
        ) : (
          <div>
            {monitorEvents?.items?.map((event, index) => (
              <TableRow
                key={event.id}
                align="center"
                style={{
                  backgroundColor:
                    selectedEvent?.id === event.id
                      ? theme.colors.primary20
                      : "transparent",
                }}
              >
                <Col
                  lg={3}
                  style={{ display: "flex", alignItems: "center", gap: 16 }}
                >
                  <CircleIcon type={event.handled ? "success" : "error"} />
                  <div>
                    <BodySmall>{event.clientName}</BodySmall>
                    <Small>{event.clientIdentifier}</Small>
                  </div>
                </Col>
                <Col lg={3}>
                  <BodySmall>
                    {t(
                      getMonitorEventTitles(event.eventType) ?? event.eventType,
                    )}
                  </BodySmall>
                </Col>
                <Col lg={2}>
                  <BodySmall>
                    {dayjs(event.createdAt).format("YYYY-MM-DD")}
                  </BodySmall>
                </Col>
                <Col lg={3}>
                  {event.comments &&
                    event.comments?.items?.map((comment) => (
                      <BodySmall>{comment.content}</BodySmall>
                    ))}
                </Col>
                <Col lg={1}>
                  <div
                    style={{
                      justifyContent: "flex-end",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <HandleMonitorEventButton
                      onClick={() => setOpenModalIndex(index)}
                    />

                    <Tooltip
                      content={
                        <Body color={theme.colors.white}>
                          {t("documentHandler.goToClientCard")}
                        </Body>
                      }
                    >
                      <IconButton
                        icon={<OpenLinkIcon color={theme.colors.primary} />}
                        color={theme.colors.primary20}
                        onClick={() =>
                          window.open(
                            `/client/${event.clientIdentifier}/aml`,
                            "_blank",
                          )
                        }
                      />
                    </Tooltip>
                  </div>
                </Col>
              </TableRow>
            ))}
          </div>
        )}
        <PaginationComponent
          currentPageIndex={currentPage}
          numberOfPages={Math.ceil(
            (monitorEvents?.totalCount || 0) / ITEMS_PER_PAGE,
          )}
          setCurrentPageIndex={setCurrentPage}
          spacing={1}
        />
      </Card>
      
    </div>
  );
};

export default AmlMonitoringEvents;
